/**
 * Credit Purchase Modal Component
 * 
 * Modal interface for purchasing credit packages including:
 * - Credit package selection
 * - Pricing display
 * - Popular/recommended badges
 * - Payment integration (Stripe)
 * - Success/error handling
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Search, 
  Brain, 
  CreditCard, 
  Shield, 
  Star, 
  CheckCircle,
  Loader2,
  X
} from 'lucide-react';
import { useCreditPackages } from '../../hooks/useUsageTracking';
import type { CreditPackage } from '../../types/usage';

interface CreditPurchaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPurchaseComplete?: (packageId: string, amount: number) => void;
  recommendedType?: 'search' | 'ai_analysis' | null;
}

export function CreditPurchaseModal({
  open,
  onOpenChange,
  onPurchaseComplete,
  recommendedType = null
}: CreditPurchaseModalProps) {
  const { packages, loading, error } = useCreditPackages();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  const handlePackageSelect = (pkg: CreditPackage) => {
    setSelectedPackage(pkg);
    setPurchaseError(null);
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    setPurchasing(true);
    setPurchaseError(null);

    try {
      // TODO: Integrate with Stripe payment processing
      // This would typically:
      // 1. Create payment intent
      // 2. Show Stripe payment form
      // 3. Process payment
      // 4. Add credits to user account
      
      // For now, simulate the purchase
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success
      onPurchaseComplete?.(selectedPackage.id, selectedPackage.price);
      onOpenChange(false);
      setSelectedPackage(null);
      
    } catch (error) {
      setPurchaseError('Payment failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const getPackagePopularity = (pkg: CreditPackage) => {
    if (pkg.name === 'top_up_30') return 'popular';
    if (pkg.name === 'top_up_50') return 'best-value';
    return null;
  };

  const getPopularityBadge = (popularity: string | null) => {
    switch (popularity) {
      case 'popular':
        return (
          <Badge className="absolute -top-2 -right-2 bg-orange-500 hover:bg-orange-600">
            <Star className="w-3 h-3 mr-1" />
            Popular
          </Badge>
        );
      case 'best-value':
        return (
          <Badge className="absolute -top-2 -right-2 bg-green-500 hover:bg-green-600">
            <CheckCircle className="w-3 h-3 mr-1" />
            Best Value
          </Badge>
        );
      default:
        return null;
    }
  };

  const isRecommendedForType = (pkg: CreditPackage) => {
    if (!recommendedType) return false;
    if (recommendedType === 'search' && pkg.search_credits >= 100) return true;
    if (recommendedType === 'ai_analysis' && pkg.ai_analysis_credits >= 20) return true;
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" />
              Purchase Credits
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Recommendation Alert */}
          {recommendedType && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertDescription className="text-blue-800">
                💡 You're running low on{' '}
                {recommendedType === 'search' ? 'search' : 'AI analysis'} usage.
                We recommend packages with more{' '}
                {recommendedType === 'search' ? 'search' : 'AI analysis'} credits.
              </AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2">Loading credit packages...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-700">
                Failed to load credit packages: {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Package Selection */}
          {!loading && !error && packages && (
            <>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Choose a credit package:</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {packages.map((pkg) => {
                    const popularity = getPackagePopularity(pkg);
                    const isRecommended = isRecommendedForType(pkg);
                    const isSelected = selectedPackage?.id === pkg.id;
                    
                    return (
                      <Card
                        key={pkg.id}
                        className={`relative cursor-pointer transition-all ${
                          isSelected
                            ? 'ring-2 ring-blue-500 border-blue-500'
                            : 'hover:border-gray-300'
                        } ${
                          isRecommended
                            ? 'border-blue-300 bg-blue-50/30'
                            : ''
                        }`}
                        onClick={() => handlePackageSelect(pkg)}
                      >
                        {getPopularityBadge(popularity)}
                        
                        {isRecommended && (
                          <Badge 
                            variant="outline" 
                            className="absolute -top-2 -left-2 bg-blue-100 border-blue-300 text-blue-700"
                          >
                            Recommended
                          </Badge>
                        )}

                        <CardHeader className="pb-3">
                          <CardTitle className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              ${pkg.price.toFixed(0)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {pkg.display_name}
                            </div>
                          </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-3">
                          {/* Search Credits */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-blue-500" />
                              <span className="text-sm">Searches</span>
                            </div>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {pkg.search_credits}
                            </Badge>
                          </div>

                          {/* AI Analysis Credits */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Brain className="w-4 h-4 text-purple-500" />
                              <span className="text-sm">AI Analysis</span>
                            </div>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700">
                              {pkg.ai_analysis_credits}
                            </Badge>
                          </div>

                          <Separator />

                          {/* Value Calculation */}
                          <div className="text-center text-xs text-muted-foreground">
                            ~${(pkg.price / (pkg.search_credits + pkg.ai_analysis_credits * 5)).toFixed(3)} per search
                          </div>

                          {/* Selection Indicator */}
                          {isSelected && (
                            <div className="flex items-center justify-center gap-2 text-blue-600 text-sm font-medium">
                              <CheckCircle className="w-4 h-4" />
                              Selected
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Payment Section */}
              {selectedPackage && (
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold">Payment Details</h3>
                  
                  <Card className="bg-gray-50">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Package:</span>
                          <span>{selectedPackage.display_name}</span>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span>Search Credits:</span>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            {selectedPackage.search_credits}
                          </Badge>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span>AI Analysis Credits:</span>
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            {selectedPackage.ai_analysis_credits}
                          </Badge>
                        </div>
                        
                        <Separator />
                        
                        <div className="flex justify-between items-center text-lg font-semibold">
                          <span>Total:</span>
                          <span className="text-green-600">
                            ${selectedPackage.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Security Notice */}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="w-4 h-4 text-green-500" />
                    <span>Secure payment processing with Stripe</span>
                  </div>

                  {/* Purchase Error */}
                  {purchaseError && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertDescription className="text-red-700">
                        {purchaseError}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={purchasing}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePurchase}
                      disabled={purchasing}
                      className="min-w-[140px]"
                    >
                      {purchasing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Purchase Credits
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Features & Benefits */}
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold mb-3">What you get:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Credits never expire</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Use for searches or AI analysis</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Instant activation after payment</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No subscription required</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}